import { Link } from "react-router-dom";
import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, LoadingState, ErrorState, StatusBadge, EmptyState } from "@/components/common";
import { PhoneBookingDialog, AppointmentSheet } from "@/modules/scheduling/AppointmentDialogs";
import { ymdIn, addDays, fmtTime, fmtDayLong, minutesIn } from "@/modules/scheduling/shared";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { CalendarDays } from "lucide-react";
import { t } from "@/i18n";

const PX = 64;
const apptTip = (a, tz) => `${a.open_ended ? t("b11.dropoffAt", { time: fmtTime(a.start, tz) }) : `${fmtTime(a.start, tz)}–${fmtTime(a.end, tz)}`} · ${a.service_name} · ${a.customer_name}`;
const cardCls = (a) => ["pending", "awaiting_parts"].includes(a.status) ? "border-dashed border-amber-400 bg-amber-50 text-amber-950" : "border-teal-300 bg-teal-50 text-teal-950";

function DayView({ data, day, tz, onOpen }) {
  const hours = data.staff_hours.filter((h) => h.date === day);
  const all = hours.flatMap((h) => h.intervals);
  const from = Math.floor(Math.min(420, ...all.map((i) => i[0])) / 60) * 60;
  const to = Math.ceil(Math.max(1080, ...all.map((i) => i[1])) / 60) * 60;
  const y = (m) => ((m - from) / 60) * PX;
  if (!data.staff.length) return <EmptyState icon={CalendarDays} title={t("sched.noStaff")} text={t("sched.noStaffText")} />;
  return (
    <div className="gf-card overflow-x-auto" data-testid="planning-day-view">
      <div className="grid min-w-[640px]" style={{ gridTemplateColumns: `56px repeat(${data.staff.length}, minmax(180px, 1fr))` }}>
        <div />
        {data.staff.map((s) => <div key={s.user_id} className="border-b border-l border-slate-200 p-3 text-sm font-semibold" data-testid={`planning-col-${s.name.split(" ")[0].toLowerCase()}`}>{s.name}</div>)}
        <div className="relative" style={{ height: y(to) }}>
          {Array.from({ length: (to - from) / 60 }, (_, i) => <span key={i} className="absolute right-2 text-[11px] text-slate-400" style={{ top: i * PX - 6 }}>{String(from / 60 + i).padStart(2, "0")}:00</span>)}
        </div>
        {data.staff.map((s) => {
          const iv = hours.find((h) => h.user_id === s.user_id)?.intervals || [];
          return (
            <div key={s.user_id} className="relative border-l border-slate-200 bg-slate-100/70" style={{ height: y(to) }}>
              {Array.from({ length: (to - from) / 60 }, (_, i) => <div key={i} className="absolute inset-x-0 border-t border-slate-200/70" style={{ top: i * PX }} />)}
              {iv.map(([a, b]) => <div key={a} className="absolute inset-x-0 bg-white" style={{ top: y(a), height: y(b) - y(a) }} />)}
              {data.blocks.filter((b) => !b.user_id || b.user_id === s.user_id).filter((b) => ymdIn(tz, new Date(b.start)) === day).map((b) => (
                <div key={b.id} className="absolute inset-x-1 overflow-hidden rounded-md border border-slate-300 bg-[repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9_6px,#e2e8f0_6px,#e2e8f0_12px)] p-1.5 text-[11px] text-slate-700"
                  style={{ top: y(minutesIn(b.start, tz)), height: Math.max(20, y(minutesIn(b.end, tz)) - y(minutesIn(b.start, tz))) }} data-testid={`planning-block-${b.id}`}>
                  <StatusBadge status={b.kind} /> {b.reason}
                </div>
              ))}
              {data.appointments.filter((a) => a.mechanic_user_id === s.user_id && ymdIn(tz, new Date(a.start)) === day).map((a) => (
                <button key={a.id} type="button" onClick={() => onOpen(a)} data-testid={`planning-appt-${a.id}`} title={apptTip(a, tz)}
                  className={`absolute inset-x-1 overflow-hidden rounded-md border p-1.5 text-left text-xs transition-shadow duration-150 hover:shadow-md ${cardCls(a)}`}
                  style={{ top: y(minutesIn(a.start, tz)), height: Math.max(28, y(minutesIn(a.block_end, tz)) - y(minutesIn(a.start, tz)) - 2) }}>
                  <p className="font-semibold">{fmtTime(a.start, tz)} {a.service_name}</p>
                  <p className="truncate">{a.customer_name} · {a.vehicle}</p>
                  <p>{t(`status.${a.status}`)}{a.proposal ? ` · ${t("status.proposed")}` : ""}</p>
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ data, start, tz, onOpen }) {
  return (
    <div className="grid gap-3 md:grid-cols-7" data-testid="planning-week-view">
      {Array.from({ length: 7 }, (_, i) => addDays(start, i)).map((d) => {
        const list = data.appointments.filter((a) => ymdIn(tz, new Date(a.start)) === d);
        const blocks = data.blocks.filter((b) => ymdIn(tz, new Date(b.start)) === d);
        return (
          <div key={d} className="gf-card min-h-40 p-3" data-testid={`planning-week-day-${d}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">{fmtDayLong(d, tz)}</p>
            {list.length === 0 && blocks.length === 0 && <p className="text-xs text-slate-400">{t("sched.nothing")}</p>}
            {blocks.map((b) => <p key={b.id} className="mb-1 rounded bg-slate-100 p-1.5 text-[11px] text-slate-700">{fmtTime(b.start, tz)}–{fmtTime(b.end, tz)} {b.reason}</p>)}
            {list.map((a) => (
              <button key={a.id} type="button" onClick={() => onOpen(a)} title={apptTip(a, tz)} className={`mb-1.5 w-full rounded-md border p-1.5 text-left text-xs ${cardCls(a)}`} data-testid={`planning-week-appt-${a.id}`}>
                <p className="font-semibold">{fmtTime(a.start, tz)} {a.service_name}</p>
                <p className="truncate">{a.customer_name} · {a.mechanic_name}</p>
              </button>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function Planning() {
  const { garage, readOnly, isManager } = useGarage();
  const tz = garage.timezone;
  const [view, setView] = useState("day");
  const [day, setDay] = useState(ymdIn(tz));
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const [sel, setSel] = useState(null);
  const { data, error, loading, reload } = useFetch("/planning", { start: day, days: view === "week" ? 7 : 1 });
  const refresh = () => { setSel(null); reload(); };
  return (
    <div>
      <PageHeader title={t("sched.planning")} subtitle={t("sched.planningHint")}
        actions={<>{isManager && <Button asChild variant="outline" data-testid="planning-manage-services"><Link to="/pro/settings?tab=services">{t("services.manage")}</Link></Button>}
          {!readOnly && <Button onClick={() => setOpen(true)} data-testid="new-phone-booking-button"><Plus className="mr-2 h-4 w-4" /> {t("sched.phoneBooking")}</Button>}</>} />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {["day", "week"].map((v) => <button key={v} type="button" onClick={() => setView(v)} data-testid={`planning-view-${v}`} className={`rounded-md px-3 py-1.5 text-sm font-medium ${view === v ? "bg-[#0A2540] text-white" : "text-slate-600"}`}>{t(`sched.view.${v}`)}</button>)}
        </div>
        <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, view === "week" ? -7 : -1))} data-testid="planning-prev" aria-label={t("sched.prevDay")}><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setDay(ymdIn(tz))} data-testid="planning-today">{t("sched.today")}</Button>
        <Button variant="outline" size="icon" onClick={() => setDay(addDays(day, view === "week" ? 7 : 1))} data-testid="planning-next" aria-label={t("sched.nextDay")}><ChevronRight className="h-4 w-4" /></Button>
        <span className="text-sm font-semibold text-slate-700" data-testid="planning-date-label">{fmtDayLong(day, tz)}</span>
        <span className="ml-auto flex gap-2 text-xs"><StatusBadge status="confirmed" /><StatusBadge status="pending" /></span>
      </div>
      {loading && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && !loading && (view === "day" ? <DayView data={data} day={day} tz={tz} onOpen={setSel} /> : <WeekView data={data} start={day} tz={tz} onOpen={setSel} />)}
      {open && <PhoneBookingDialog open={open} onOpenChange={setOpen} tz={tz} onDone={() => { setOpen(false); reload(); }} />}
      <AppointmentSheet appt={sel} tz={tz} readOnly={readOnly} onClose={() => setSel(null)} onChanged={refresh} />
    </div>
  );
}
